import { useCallback } from "react";
import { useVerificationStoreApi } from "../state/store-context";
import { usePreferences } from "@/hooks/usePreferences";

/**
 * E-049 — cycle the snippet zoom (1x → 2x → 3x → auto) AND persist the
 * cycled choice to OperatorPreference.settings.snippetZoomDefault so "set it
 * once, keep it" holds for snippets too. Previously only the Operator
 * Preferences page select wrote the preference; the in-station cycle
 * (F2 / Ctrl+; / SnippetPanel button) was store-only and reset on unmount.
 *
 * Mirrors useViewerFitModePersist (R-198): an explicit-action callback, not a
 * passive watcher — so useApplyPreferences restoring the persisted default on
 * mount never re-persists it. usePreferences applies its own 300ms debounce +
 * optimistic localStorage write, so no extra debounce is needed here.
 *
 * Uses the pane store API (not the module singleton) so the QC/supervisor
 * review pane (VerificationStoreProvider) persists correctly too.
 */
export function useCycleSnippetZoomPersist(): () => void {
  const storeApi = useVerificationStoreApi();
  const { updatePreference } = usePreferences();

  return useCallback(() => {
    storeApi.getState().cycleSnippetZoom();
    updatePreference("snippetZoomDefault", storeApi.getState().snippetZoom);
  }, [storeApi, updatePreference]);
}
